import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import StatusBadge from "../../components/StatusBadge";
import Timeline from "../../components/Timeline";
import ProjectDossier from "../../components/dossier/ProjectDossier";
import { WIN_STAGES } from "../../lib/constants";

export default function ProjectDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    api.get(`/projects/${pid}`).then((r) => {
      setP(r.data);
      // Ouverture du projet → pastille « ACA vous a répondu » effacée (les échanges restent enregistrés).
      if ((r.data.unread_replies || 0) > 0) api.post(`/projects/${pid}/messages/read`).catch(() => {});
    }).catch(() => nav("/app/projets"));
  }, [pid]);
  if (!p) return <div className="py-20 text-center text-ink-secondary">{t("u_loading")}</div>;

  const isWinnerStage = WIN_STAGES.includes(p.status);
  const globalProgress = p.dev_phases ? Math.round(p.dev_phases.reduce((a, b) => a + (b.progress || 0), 0) / p.dev_phases.length) : 0;

  return (
    <div className="mx-auto max-w-5xl">
      <Link to="/app/projets" className="inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white"><ArrowLeft size={15} /> {t("u_back_projects")}</Link>

      {(p.unread_replies || 0) > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-cyan/40 bg-cyan/10 px-4 py-3 text-sm font-600 text-cyan" data-testid="project-unread-banner">
          <Bell size={15} className="animate-pulse" /> ACA vous a répondu — consultez l'onglet « Échanges » du suivi ci-dessous.
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-8">
        <div className="flex items-center gap-4">
          {p.logo ? <img src={p.logo} alt="" className="h-16 w-16 object-contain" /> : <div className="flex h-16 w-16 items-center justify-center bg-elevated font-display text-2xl text-cyan">{(p.app_name || p.title || "?")[0]}</div>}
          <div>
            <h1 className="font-display text-3xl font-900 text-white">{p.app_name || p.title}</h1>
            <div className="mt-1 text-sm text-ink-tertiary">{p.ref} {p.slogan && `· ${p.slogan}`}</div>
          </div>
        </div>
        <StatusBadge status={p.status} className="text-xs" />
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <div className="space-y-8">
          <Section title={t("u_idea")} body={p.title} />
          <Section title={t("u_description")} body={p.description} />
          {p.problem && <Section title={t("u_problem")} body={p.problem} />}
          <div className="grid grid-cols-2 gap-4">
            <Meta label={t("u_audience")} value={p.audience} />
            <Meta label={t("u_type")} value={p.app_type} />
            <Meta label={t("u_category")} value={p.category} />
            <Meta label={t("u_advancement")} value={p.advancement} />
          </div>
          {p.documents?.length > 0 && (
            <div>
              <div className="mb-3 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("u_docs")}</div>
              <div className="flex flex-wrap gap-2">
                {p.documents.map((d, i) => <span key={i} className="border border-white/10 bg-surface px-3 py-1.5 text-xs text-ink-secondary">{d.name}</span>)}
              </div>
            </div>
          )}

          {isWinnerStage && p.dev_phases && (
            <div className="border border-cyan/30 bg-surface p-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-lg font-700 text-white">{t("u_my_app")}</span>
                <span className="font-display text-3xl font-900 text-cyan">{globalProgress}%</span>
              </div>
              <div className="mt-5 space-y-3">
                {p.dev_phases.map((ph, i) => (
                  <div key={i}>
                    <div className="mb-1 flex justify-between text-sm"><span className="text-ink-secondary">{ph.name}</span><span className="tabular-nums text-cyan">{ph.progress}%</span></div>
                    <div className="h-1.5 bg-elevated"><div className="h-full bg-cyan" style={{ width: `${ph.progress}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="mb-5 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{t("u_tracking")}</div>
          <Timeline status={p.status} />
        </div>
      </div>

      {/* Suivi de mon projet — dossier collaboratif (actif si le projet est sélectionné) */}
      <ProjectDossier pid={pid} mode="owner" project={p} />
    </div>
  );
}

const Section = ({ title, body }) => (
  <div>
    <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">{title}</div>
    <p className="text-base leading-relaxed text-white">{body || "—"}</p>
  </div>
);
const Meta = ({ label, value }) => (
  <div className="border border-white/10 bg-surface p-4">
    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</div>
    <div className="mt-1 text-sm text-white">{value || "—"}</div>
  </div>
);
